import Button from '@/components/Button/Button';
import Header from '@/components/Header/Header';
import { useSTKContext } from '@/context/context';
import {
  getDinamicHeaders,
  getStoreFilteredProducts,
  getStoreGroups,
  getStores,
} from '@/services/menu/menus.helper.service';
import { useMenuProductsGroup, useProductCategories } from '@/services/menu/menus.service';
import { FilteredValues, StoreProduct } from '@/types/menus.types';
import { Box, Chip } from '@mui/material';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FZStoreMenuDetails.module.css';
import FZStoreItemTable from './internal/FZStoreItemTable';
import FZStoreMenuFilter from './internal/FZStoreMenuFilter';
import ModalStores from './internal/ModalStores.module';

import Tooltip from '@/components/Tooltip/Tooltip';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useCurrentUser } from '@/services/user/user.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { RowSelectionState } from '@tanstack/react-table';
import classNames from 'classnames';
import { EditPriceDrawer } from '../EditPriceDrawer/EditPriceDrawer';
import { UserRoles, UserTypes } from '@/types/auth.types';
import { ProductPriceDeviationsDrawer } from '../PriceDeviationsDrawer/ProductPriceDeviationsDrawer';
import { DeactivateProductsDrawer } from '../DeactivateProductsDrawer/DeactivateProductsDrawer';
import { ButtonVariants } from '@/components/Button/Button.types';
import tooltipStyles from '@/components/Tooltip/Tooltip.module.css';
import Icon from '@/components/Icons/Icon';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

const DEFAULT_CATEGORY_NAME = 'Pizza';
const provideNewSelectedRows = (selectedRows: RowSelectionState | undefined, storeProducts: StoreProduct[]) => {
  const selectedStores = new Set(Object.keys(selectedRows as RowSelectionState));
  const newSelectedRows: StoreProduct[] = [];
  selectedStores.forEach((index) => {
    const product = storeProducts[+index];
    if (product) {
      // Safety check to ensure product exists
      newSelectedRows.push(product);
    }
  });
  return newSelectedRows;
};

// Helper function to check if a product has NO price across all store groups (completely inactive)
const isProductCompletelyInactive = (product: StoreProduct, selectedStoreGroupNames: string[]): boolean => {
  // Safety check for undefined product
  if (!product) {
    return true;
  }
  // If product has no store groups, it's completely inactive
  if (!product.storeGroups || product.storeGroups.length === 0) {
    return true;
  }
  // If no store groups are selected, consider it completely inactive
  if (!selectedStoreGroupNames || selectedStoreGroupNames.length === 0) {
    return true;
  }

  // Check if product has NO price in any of the selected store groups
  return selectedStoreGroupNames.every((selectedGroupName) => {
    const storeGroup = product.storeGroups.find((sg) => sg.storeGroupName === selectedGroupName);
    return (
      !storeGroup ||
      !storeGroup.chosenPriceRange ||
      storeGroup.chosenPriceRange.trim() === '' ||
      storeGroup.chosenPriceRange === 'Inactive'
    );
  });
};

// Component to wrap a button with a tooltip using Floating UI
const ButtonWithTooltip: React.FC<{
  showTooltip: boolean;
  tooltipContent: string;
  button: React.ReactElement;
}> = ({ showTooltip, tooltipContent, button }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  if (!showTooltip) {
    return button;
  }

  return (
    <>
      <span ref={reference} {...getReferenceProps()} style={{ display: 'inline-block' }}>
        {button}
      </span>
      <FloatingPortal>
        {open && (
          <div
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            className={tooltipStyles.tooltipContainer}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={tooltipStyles.tooltipArrow}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide!]: '-4px',
              }}
            />
            <div
              style={{
                fontSize: '2.25rem',
                textAlign: 'justify',
                lineHeight: '3rem',
                maxWidth: '100%',
                wordWrap: 'break-word',
              }}
            >
              {tooltipContent}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

const FZStoreMenuDetails = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { storesAndGroups } = useSTKContext();
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const [isEditPriceDrawerOpen, setIsEditPriceDrawerOpen] = useState<boolean>(false);
  const [isDeactivateDrawerOpen, setIsDeactivateDrawerOpen] = useState<boolean>(false);
  const [priceDeviationsData, setPriceDeviationsData] = useState({
    isPriceDeviationsDrawerOpen: false,
    variantIds: [],
    selectedGroupName: '',
    cameFromEditDrawer: false,
  });
  const [storeProducts, setStoreProducts] = useState<StoreProduct[]>();
  const { data: dataUser } = useCurrentUser(true);
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);

  const isFranchiseeUserEdit = dataUser?.data.type === UserTypes.FRANCHISEE && dataUser?.data.role === UserRoles.EDIT;
  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const isFranchiseeEditAndFreeze = isFranchiseeUserEdit && freezeExists;

  const [filteredValues, setFilteredValues] = useState<FilteredValues>({});
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [filterApplied, setFilterApplied] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState<string>(DEFAULT_CATEGORY_NAME);
  const [subCategories, setSubCategories] = useState<
    | {
        value: string;
        label: string;
      }[]
    | undefined
  >([]);

  const { data: productCategories = [], isLoading: isLoadingCategories = false } = useProductCategories(
    true,
    stores,
    [],
  );
  const {
    data: menuProductsGroup = [],
    isLoading: isStoreLoading,
    isFetching,
    refetch: refetchProducts,
  } = useMenuProductsGroup(filterApplied, stores, storeGroups, categoryName);

  const [headerWidth, setHeaderWidth] = useState('');

  const changeHeaderWidth = (newWidth: string) => {
    setHeaderWidth(newWidth);
  };
  const applyFilter = (pFilteredValues: FilteredValues) => {
    setFilteredValues(pFilteredValues);
    setFilterApplied(true);
    setSelectedRows({});
  };
  const handleEditPriceDrawerClose = () => {
    setIsEditPriceDrawerOpen(false);
  };

  const handlePriceDeviationsDrawerClose = () => {
    setPriceDeviationsData({ ...priceDeviationsData, isPriceDeviationsDrawerOpen: false });
  };

  const clearFilter = () => {
    setFilteredValues({});
    setCategoryName(categoryName);
  };

  const categories = productCategories.map((productCategory) => ({
    value: productCategory.category,
    label: productCategory.category,
  }));

  const products = useMemo(
    () => getStoreFilteredProducts(menuProductsGroup, filteredValues),
    [menuProductsGroup, filteredValues],
  );

  useEffect(() => {
    const categoryWithSubCategory = productCategories.find(
      (productCategory) => productCategory.category === categoryName,
    );
    if (categoryWithSubCategory) {
      const subcats = categoryWithSubCategory.subcategories?.map((productCategory) => ({
        value: productCategory.productClassCode,
        label: productCategory.subcategory,
      }));
      setSubCategories(subcats);
    }
  }, [categoryName, productCategories]);

  const handleEditPriceDrawerOpen = useCallback(() => {
    const selectedProducts = provideNewSelectedRows(selectedRows, products);
    setStoreProducts(selectedProducts);
    setIsEditPriceDrawerOpen(true);
  }, [products, selectedRows]);

  const handleDeactivateDrawerOpen = useCallback(() => {
    const selectedProducts = provideNewSelectedRows(selectedRows, products);
    setStoreProducts(selectedProducts);
    setIsDeactivateDrawerOpen(true);
  }, [products, selectedRows]);

  const handleDeactivateDrawerClose = () => {
    setIsDeactivateDrawerOpen(false);
  };

  const shouldDisableButtonForPriceLockProducts = useCallback(() => {
    const selectedProducts = provideNewSelectedRows(selectedRows, products);
    return selectedProducts.some((product) => {
      // Check if product has price lock
      if (product.priceLock) {
        // Check if any store group has "Inactive-" prefix or numeric value in chosenPriceRange
        return product.storeGroups.some((storeGroup) => {
          const { chosenPriceRange } = storeGroup;
          // Check for "Inactive-" prefix or exact numeric value match
          return (
            (chosenPriceRange as string).startsWith('Inactive-') || /^\d+(\.\d+)?$/.test(chosenPriceRange as string)
          ); // matches patterns like "10.69", "10", "0.50", etc.
        });
      }
      return false;
    });
  }, [selectedRows, products]);

  const handleCategory = (newCategory: string) => {
    setFilteredValues({});
    setCategoryName(newCategory);
  };

  useEffect(() => {
    setSelectedRows({});
  }, [categoryName]);

  // Get selected store group names from context (same logic as getDinamicHeaders)
  const selectedStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }
    if (storesAndGroups.origin === 'tab-stores-groups') {
      return storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || [];
    }
    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet);
    }
    return [];
  }, [storesAndGroups]);

  // Calculate button states for Deactivate Products button
  const selectedProducts = useMemo(() => {
    const selectedProductsList = provideNewSelectedRows(selectedRows, products);
    // Filter out any undefined products as an extra safety measure
    return selectedProductsList.filter((product) => product != null);
  }, [selectedRows, products]);

  // Check for products that are completely inactive (no price at all)
  const hasCompletelyInactiveProducts = useMemo(
    () => selectedProducts.some((product) => product && isProductCompletelyInactive(product, selectedStoreGroupNames)),
    [selectedProducts, selectedStoreGroupNames],
  );

  const hasMandatoryProducts = useMemo(
    () => selectedProducts.some((product) => product && product.mandatoryProductLock === true),
    [selectedProducts],
  );

  // Button disabled if no products selected OR if any completely inactive product is selected OR if any mandatory product is selected
  const isDeactivateButtonDisabled = useMemo(
    () => Object.keys(selectedRows).length < 1 || hasCompletelyInactiveProducts || hasMandatoryProducts,
    [selectedRows, hasCompletelyInactiveProducts, hasMandatoryProducts],
  );

  // Button disabled with tooltip if any completely inactive product is selected OR if any mandatory product is selected
  const shouldShowTooltip = useMemo(
    () => hasCompletelyInactiveProducts || hasMandatoryProducts,
    [hasCompletelyInactiveProducts, hasMandatoryProducts],
  );

  // Determine tooltip message based on what type of products are selected
  const tooltipMessage = useMemo(() => {
    if (hasMandatoryProducts) {
      return t('deactivateProducts.mandatoryProductsSelectedTooltip');
    }
    if (hasCompletelyInactiveProducts) {
      return t('deactivateProducts.inactiveProductsSelectedTooltip');
    }
    return '';
  }, [hasMandatoryProducts, hasCompletelyInactiveProducts, t]);

  // Calculate button states for Edit Price button - check for price locked products
  const hasPriceLockedProducts = useMemo(() => {
    return selectedProducts.some((product) => {
      // Check if product has price lock
      if (product.priceLock) {
        // Check if any store group has "Inactive-" prefix or numeric value in chosenPriceRange
        return product.storeGroups.some((storeGroup) => {
          const { chosenPriceRange } = storeGroup;
          // Check for "Inactive-" prefix or exact numeric value match
          return (
            (chosenPriceRange as string).startsWith('Inactive-') || /^\d+(\.\d+)?$/.test(chosenPriceRange as string)
          ); // matches patterns like "10.69", "10", "0.50", etc.
        });
      }
      return false;
    });
  }, [selectedProducts]);

  // Button disabled with tooltip if any price locked product is selected
  const shouldShowEditPriceTooltip = useMemo(() => hasPriceLockedProducts, [hasPriceLockedProducts]);

  return (
    <div className={styles.page} data-testid="fzStoreMenu">
      <div>
        <div style={{ minWidth: 'fit-content' }}>
          <Header title="Menu" breadcrumb={{ label: t('breadcrumb.backToAllMenus'), to: '/menus' }} />
        </div>
        <div className={styles.chip}>
          <Chip
            data-testid="icon"
            key="storeCount"
            label={t('menu.storesLabel', { count: stores?.length ?? 1 })}
            color="default"
            variant="outlined"
            onClick={() => setModalOpen(!modalOpen)}
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              height: '28px',
              backgroundColor: 'white',
              color: '#266DCC',
              borderColor: '#266DCC',
              marginLeft: '160px',
              marginTop: '-135px',
            }}
          />
        </div>
      </div>
      <div className={classNames(styles.header, { [styles.withAlias]: alias })} style={{ minWidth: headerWidth }}>
        <div className={styles.headerLabelAndLegendContainer}>
          <span>{t('menu.filter.products')}</span>
          <span className={styles.legend}>
            <Tooltip
              position="top"
              size={22}
              content={
                <div className={styles.contentColor}>
                  <div className={styles.labelPosition}>{t('menu.legend.dealLovers')}</div>
                  <div>{t('menu.legend.virtualBrand')}</div>
                </div>
              }
            />
            <span className={styles.legendLabel}>{t('menu.legend.legend')}</span>
          </span>
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <div className={styles.headerButtonsContainer}>
              <ButtonWithTooltip
                showTooltip={shouldShowTooltip}
                tooltipContent={tooltipMessage}
                button={
                  <Button
                    variant={ButtonVariants.Primary}
                    onClick={handleDeactivateDrawerOpen}
                    disabled={isDeactivateButtonDisabled}
                  >
                    {shouldShowTooltip && (
                      <Icon
                        iconName="AlertCircle"
                        size={20}
                        style={{ marginRight: '8px', color: 'var(--white)', stroke: 'var(--white)' }}
                      />
                    )}
                    {t('deactivateProducts.title')}
                  </Button>
                }
              />
              <ButtonWithTooltip
                showTooltip={shouldShowEditPriceTooltip}
                tooltipContent={t('menu.tooltips.editPriceButtonDisabled')}
                button={
                  <Button
                    className={styles.editPriceButton}
                    onClick={handleEditPriceDrawerOpen}
                    disabled={Object.keys(selectedRows).length < 1 || shouldDisableButtonForPriceLockProducts()}
                  >
                    {shouldShowEditPriceTooltip && (
                      <Icon
                        iconName="AlertCircle"
                        size={20}
                        style={{ marginRight: '8px', color: 'var(--white)', stroke: 'var(--white)' }}
                      />
                    )}
                    {t('menu.editMenuPriceButton')}
                  </Button>
                }
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.weeklyChangesBanner}>
        <CalendarAlert warningIcon requestedVerbiage={false} addCalendarLink />
      </div>
      <div className={styles.filterActions}>
        <FZStoreMenuFilter
          onApplyFilter={(pFilteredValues: FilteredValues) => applyFilter(pFilteredValues)}
          onClearFilter={clearFilter}
          categories={categories}
          categoryName={categoryName}
          onCategoryChange={(newCategory: string) => handleCategory(newCategory)}
          subCategories={subCategories || []}
          isLoadingCategories={isLoadingCategories}
          searching={isFetching}
        />
      </div>
      {filterApplied && !isStoreLoading && (
        <Box>
          <Box>
            {isStoreLoading || products.length === 0 ? (
              <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
                <p> No products match your search for these stores </p>
              </Box>
            ) : (
              <FZStoreItemTable
                menuProducts={products}
                dynamicHeaders={getDinamicHeaders(storesAndGroups)}
                setPriceDeviationsData={setPriceDeviationsData}
                selectedRows={selectedRows}
                onRowSelectionChange={setSelectedRows}
                isLoading={false}
                changeHeaderSize={changeHeaderWidth}
                storesAndGroups={storesAndGroups}
                isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
              />
            )}
          </Box>
        </Box>
      )}

      {modalOpen && <ModalStores stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />}

      {isEditPriceDrawerOpen && (
        <EditPriceDrawer
          isOpen={isEditPriceDrawerOpen}
          onClose={handleEditPriceDrawerClose}
          storeProducts={storeProducts || []}
          afterSave={() => {
            setSelectedRows({});
            refetchProducts();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
        />
      )}
      {priceDeviationsData.isPriceDeviationsDrawerOpen && (
        <ProductPriceDeviationsDrawer
          isOpen={priceDeviationsData.isPriceDeviationsDrawerOpen}
          onClose={handlePriceDeviationsDrawerClose}
          variantIds={priceDeviationsData.variantIds}
          selectedGroupName={priceDeviationsData.selectedGroupName}
          cameFromEditDrawer={priceDeviationsData.cameFromEditDrawer}
        />
      )}
      {isDeactivateDrawerOpen && (
        <DeactivateProductsDrawer
          isOpen={isDeactivateDrawerOpen}
          onClose={handleDeactivateDrawerClose}
          storeProducts={storeProducts || []}
          afterSave={() => {
            setSelectedRows({});
            refetchProducts();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
        />
      )}
    </div>
  );
};
export default FZStoreMenuDetails;
